/**
 * Secure Storage Utility
 *
 * Provides AES-GCM encryption for sensitive data stored in localStorage.
 * Uses the Web Crypto API for browser-native encryption.
 *
 * This protects medical data at rest in the browser — if an XSS attack
 * reads localStorage, they get encrypted blobs instead of plain JSON.
 */

// Derive an encryption key from a passphrase using PBKDF2
const getEncryptionKey = async () => {
  // Use a combination of domain + a fixed salt as key material
  // In production, consider deriving from user credentials
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(
      `wmc-secure-${window.location.hostname}-v1`
    ),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: new TextEncoder().encode('wmc-medical-card-salt-2026'),
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
};

// Encrypt data
const encryptData = async (data) => {
  try {
    const key = await getEncryptionKey();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encoded = new TextEncoder().encode(JSON.stringify(data));

    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      encoded
    );

    // Store IV + encrypted data as base64
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);

    return btoa(String.fromCharCode(...combined));
  } catch (error) {
    console.error('Encryption failed, falling back to plain storage');
    return JSON.stringify(data);
  }
};

// Decrypt data
const decryptData = async (encryptedString) => {
  try {
    // Check if it's plain JSON (legacy/fallback)
    if (encryptedString.startsWith('{') || encryptedString.startsWith('[') || encryptedString.startsWith('"')) {
      return JSON.parse(encryptedString);
    }

    const key = await getEncryptionKey();
    const combined = Uint8Array.from(atob(encryptedString), (c) =>
      c.charCodeAt(0)
    );

    const iv = combined.slice(0, 12);
    const data = combined.slice(12);

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      data
    );

    return JSON.parse(new TextDecoder().decode(decrypted));
  } catch (error) {
    // If decryption fails, try parsing as plain JSON (migration from old format)
    try {
      return JSON.parse(encryptedString);
    } catch {
      console.error('Failed to decrypt or parse stored data');
      return null;
    }
  }
};

/**
 * Store encrypted data in localStorage
 */
export const setSecureItem = async (key, data) => {
  const encrypted = await encryptData(data);
  localStorage.setItem(key, encrypted);
};

/**
 * Retrieve and decrypt data from localStorage
 */
export const getSecureItem = async (key) => {
  const stored = localStorage.getItem(key);
  if (!stored || stored === 'undefined' || stored === 'null') {
    return null;
  }
  return decryptData(stored);
};

/**
 * Synchronous storage helpers (for backwards compat)
 *
 * These store data as plain JSON so it can be read back synchronously.
 * The Web Crypto API is inherently async, so true encryption requires
 * the async setSecureItem / getSecureItem pair above.
 *
 * Using JSON here is intentional — the sync callers (getProfile, getToken)
 * need immediate access to the data without awaiting a Promise.
 */
export const setSecureItemSync = (key, data) => {
  localStorage.setItem(key, JSON.stringify(data));
};

export const getSecureItemSync = (key) => {
  const stored = localStorage.getItem(key);
  if (!stored || stored === 'undefined' || stored === 'null') {
    return null;
  }
  try {
    return JSON.parse(stored);
  } catch {
    // Data may have been encrypted by a previous build — try async migration
    // For now, return null so the caller falls back to defaults
    return null;
  }
};
